package com.eladfinish.bible.contest.screens.pastQuizzes

import com.eladfinish.bible.contest.appcontent.AppLinks
import com.eladfinish.bible.contest.common.gender.HebrewGender
import com.eladfinish.bible.contest.platform.isAndroid
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.screens.settings.ui.screens.FONT_PREFERENCE_KEY
import com.eladfinish.bible.contest.ui.common.FONT_EZRA_REGULAR
import com.eladfinish.bible.contest.ui.common.FONT_KEREN_REGULAR
import com.eladfinish.bible.contest.ui.common.FONT_KETER_YG_MEDIUM
import com.eladfinish.bible.contest.ui.common.FONT_STAM_ASHKENAZ
import com.eladfinish.bible.contest.ui.common.FONT_STAM_SEFARAD
import com.eladfinish.bible.contest.usecase.GetRemoteConfigValueUseCase
import com.russhwolf.settings.Settings

/**
 * The "חידונים אמיתיים" button: past quizzes (district, national, head-to-head) in the district-quiz
 * web app's exam mode (`/#/exam`), shown in a WebView. Remote Config can switch it off
 * ([RemoteConfig.DISABLE_PAST_QUIZZES], a missing parameter means on), switch off its voice
 * answers ([RemoteConfig.DISABLE_PAST_QUIZZES_VOICE], the same way), switch off the app's part of
 * ראש בראש ([RemoteConfig.DISABLE_PAST_QUIZZES_DUEL], the same way; see ExamDuel.kt), switch off the
 * page's events into AppMetrica ([RemoteConfig.DISABLE_PAST_QUIZZES_APPMETRICA], the same way; see
 * ExamAnalytics.kt) and move it
 * to another address ([RemoteConfig.PAST_QUIZZES_URL]) without a release.
 */
class GetPastQuizzesConfigUseCase(
    private val getRemoteConfigValueUseCase: GetRemoteConfigValueUseCase,
) {

    // Firebase answers a missing key with false, never null, so "unset" reads as "not disabled".
    fun isDisabled(): Boolean = runCatching {
        getRemoteConfigValueUseCase.get<Boolean>(RemoteConfig.DISABLE_PAST_QUIZZES)
    }.getOrNull() ?: false

    fun isVoiceDisabled(): Boolean = runCatching {
        getRemoteConfigValueUseCase.get<Boolean>(RemoteConfig.DISABLE_PAST_QUIZZES_VOICE)
    }.getOrNull() ?: false

    fun isDuelDisabled(): Boolean = runCatching {
        getRemoteConfigValueUseCase.get<Boolean>(RemoteConfig.DISABLE_PAST_QUIZZES_DUEL)
    }.getOrNull() ?: false

    fun isAppMetricaDisabled(): Boolean = runCatching {
        getRemoteConfigValueUseCase.get<Boolean>(RemoteConfig.DISABLE_PAST_QUIZZES_APPMETRICA)
    }.getOrNull() ?: false

    fun url(
        isDarkTheme: Boolean,
        fontPreference: String,
        isVoiceEnabled: Boolean = false,
        hebrewGender: HebrewGender? = null,
        usageStats: Boolean? = null,
        duelCode: String? = null,
    ): String = pastQuizzesUrl(
        remoteBaseUrl = remoteBaseUrl(),
        isDarkTheme = isDarkTheme,
        fontPreference = fontPreference,
        isVoiceEnabled = isVoiceEnabled,
        hebrewGender = hebrewGender,
        usageStats = usageStats,
        duelCode = duelCode,
        // the screen is a WebView over the app's own parchment; only the Android one is see-through (iOS: WKWebView is opaque)
        transparentPage = isAndroid,
    )

    /** A ראש בראש room in the browser, for when the app does not open it itself. */
    fun duelBrowserUrl(duelCode: String): String = pastQuizzesDuelBrowserUrl(remoteBaseUrl = remoteBaseUrl(), duelCode = duelCode)

    private fun remoteBaseUrl(): String? = runCatching {
        getRemoteConfigValueUseCase.get<String>(RemoteConfig.PAST_QUIZZES_URL)
    }.getOrNull()
}

/**
 * The exam page for the app: `embed=1` drops the page's own header (the app shows its bar), the
 * theme and font follow the app's settings, and `voice=1` shows the page's mic, which uses the
 * app's speech recognizer (ExamVoice.kt). `voice` is always sent: the page remembers it. `gender`
 * (m / f, the user's choice of address in the app) lets the page say "אתה" or "את", "שלח" or
 * "שלחי", never "את/ה"; `stats` (1 / 0) is the user's anonymous usage-statistics consent, which
 * the page honours for its own usage events; `transparent=1` ([transparentPage]) makes the page paint
 * no background, so a transparent WebView shows the app's parchment. With [duelCode] the page opens on
 * that ראש בראש room (`#/exam/duel/{code}`) instead of the exam's home page. A remote base URL is used only when it is an
 * https address without a fragment or query, so a typo in the console cannot break the screen.
 */
internal fun pastQuizzesUrl(
    remoteBaseUrl: String?,
    isDarkTheme: Boolean,
    fontPreference: String,
    isVoiceEnabled: Boolean = false,
    hebrewGender: HebrewGender? = null,
    usageStats: Boolean? = null,
    duelCode: String? = null,
    transparentPage: Boolean = false,
): String {
    val base = examBaseUrl(remoteBaseUrl)
    val path = duelCode?.let { "/exam/duel/$it" } ?: "/exam"
    val theme = if (isDarkTheme) "dark" else "light"
    val voice = if (isVoiceEnabled) 1 else 0
    val gender = when (hebrewGender) {
        HebrewGender.MALE -> "&gender=m"
        HebrewGender.FEMALE -> "&gender=f"
        null -> ""
    }
    val stats = usageStats?.let { if (it) "&stats=1" else "&stats=0" }.orEmpty()
    val transparent = if (transparentPage) "&transparent=1" else ""
    return "$base/#$path?embed=1&theme=$theme&font=${fontPreference.toExamFontParam()}&voice=$voice$gender$stats$transparent"
}

/** The room in a browser: the page's own address, with none of the app's parameters. */
internal fun pastQuizzesDuelBrowserUrl(remoteBaseUrl: String?, duelCode: String): String =
    "${examBaseUrl(remoteBaseUrl)}/#/exam/duel/$duelCode"

private fun examBaseUrl(remoteBaseUrl: String?): String {
    val remote = remoteBaseUrl?.trim().orEmpty()
    val isValid = remote.startsWith("https://") && remote.none { it.isWhitespace() || it == '#' || it == '?' }
    return (if (isValid) remote else DEFAULT_PAST_QUIZZES_BASE_URL).trimEnd('/')
}

/**
 * The page's name for the font picked in the app's settings ([FONT_PREFERENCE_KEY]). The page
 * shows Keter YG for a font it does not carry (Keren is not licensed for the web).
 */
internal fun String.toExamFontParam(): String = when (this) {
    FONT_EZRA_REGULAR -> "ezra"
    FONT_KEREN_REGULAR -> "keren"
    FONT_STAM_ASHKENAZ -> "stam-ashkenaz"
    FONT_STAM_SEFARAD -> "stam-sefarad"
    else -> "keter"
}

/** The font picked in the app's settings, as stored ([FONT_KETER_YG_MEDIUM] by default). */
fun Settings.examFontPreference(): String = getString(key = FONT_PREFERENCE_KEY, defaultValue = FONT_KETER_YG_MEDIUM)

internal const val DEFAULT_PAST_QUIZZES_BASE_URL = AppLinks.DISTRICT_QUIZ_SITE_URL
